import { useOutletContext } from 'react-router'
import { ButtonLinkBrand } from '@/components/ui/button-link-brand'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import { Container } from '@/components/ui/container'
import { CheckCircleIcon, DownloadCloud02Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { PhotosetStatusMessage } from '@/features/photoset/components/photoset-status-message/photoset-status-message'
import { httpLogRetouchedZipDownload } from '@/features/photoset/http/photoset.http'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { useGetPhotosetImagesList } from '@/features/photoset/queries/use-get-photoset-images-list'
import type { PhotosetURLParams } from '@/features/photoset/types'
import { useAsync } from '@/hooks/use-async'
import { useTranslation } from '@/translations/i18n'

export const PhotosetImagenAiStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const { run } = useAsync()
	//queries
	const { data: photoset, isFetching: isPhotosetFetching } = useGetPhotoset(
		{ id: urlParams.id },
		{ enabled: false }
	)
	const { data: images, isFetching: isImagesFetching } =
		useGetPhotosetImagesList({
			urlParams: { photosetId: urlParams.id }
		})

	const onDownloadRetouchedLog = () => {
		run(httpLogRetouchedZipDownload(urlParams.id))
	}

	if (
		!(isPhotosetFetching || isImagesFetching || photoset?.retouched_zip_url)
	) {
		return (
			<Container className='flex flex-col items-center justify-center'>
				<PhotosetStatusMessage gap={3}>
					<Spinner className='fill-neutral-black' size={32} />
					<h1 className='font-bold text-display-xs text-neutral-900'>
						{t('photoset:imagen_ai.in_progress.title')}
					</h1>
					<p className='text-neutral-900 text-xl'>
						{t('photoset:imagen_ai.in_progress.paragraph')}
					</p>
				</PhotosetStatusMessage>
			</Container>
		)
	}

	return (
		<Container className='flex flex-col items-center justify-center'>
			{isPhotosetFetching || isImagesFetching ? (
				<Spinner className='fill-neutral-500' size={48} />
			) : (
				<>
					<CheckCircleIcon
						className='fill-success-600'
						height={48}
						width={48}
					/>

					<PhotosetStatusMessage>
						<h1 className='font-bold text-display-xs text-neutral-900'>
							{t('photoset:imagen_ai.completed.title')}
						</h1>
						<p className='text-neutral-900 text-xl'>
							{t('photoset:imagen_ai.completed.paragraph')}
						</p>
					</PhotosetStatusMessage>

					<div className='flex flex-col items-center gap-3'>
						<ButtonLinkBrand
							disabled={!photoset?.retouched_zip_url}
							onClick={() => onDownloadRetouchedLog()}
							size='lg'
							to={photoset?.retouched_zip_url ?? ''}
							type='button'
						>
							<DownloadCloud02Icon />
							{t(
								'photoset:imagen_ai.completed.download_retouched',
								{
									count: images?.filter(
										(image) => image.is_final_select
									).length
								}
							)}
						</ButtonLinkBrand>
						<ButtonLinkLegacy
							disabled={isPhotosetFetching && isImagesFetching}
							size='sm'
							to={photoset?.zip_url ?? ''}
							variant='tertiary'
						>
							{t('photoset:imagen_ai.completed.download_all_set')}
						</ButtonLinkLegacy>
					</div>
				</>
			)}
		</Container>
	)
}

PhotosetImagenAiStep.displayName = 'PhotosetImagenAiSteps'
